/**
 * Local Button — an ADAPTER onto the Signal DS Button recipe.
 *
 * ~200 app files import this component with shadcn variant/size names. Rather than edit every
 * call site, this file keeps the exact public API (`Button`, `buttonVariants`, the six variant
 * names, the eight size names, every @base-ui Button prop including `render` and
 * `focusableWhenDisabled`) and swaps only the LOOK: the classes now come from the DS's exported
 * `buttonClasses` recipe — the same one `<Button>` from @scripsteam/scrips-signal-ds renders.
 *
 * WHY THE RECIPE AND NOT THE DS <Button> COMPONENT. The DS component (2.25.0) renders a plain
 * `<button>` with no `render`/asChild seam, and wraps `children` in a single span. Call sites here
 * pass `<Icon /> Label` as children and rely on the button's flex `gap` to space them; inside one
 * span that gap disappears. Rendering the DS component would therefore restyle hundreds of
 * icon+label buttons by accident and drop the base-ui `render` prop. The recipe gives the DS look
 * with neither loss.
 *
 * TYPE + CORNERS. Every button in this app is 13px (`text-sm`, the DS 13px step), weight 500
 * (`font-medium`), no negative letter-spacing (`tracking-normal`), corners `--radius-md` (8px).
 * The DS recipe emits `font-bold tracking-[-0.02em]`, and its `row` step `text-xs` and
 * `--radius-sm`, so `TYPE` and `CORNER` are merged AFTER the recipe and win through the DS `cn`
 * (tailwind-merge). The `link` variant keeps the DS `rounded-none` (it has no box). The final
 * class strings are pinned in button-adapter.test.tsx.
 *
 * SHOWCASE LOOK (designer spec). Sizes: `default`/`sm` = Small (32 tall, 12px side padding),
 * `lg` = Medium (40 tall, 16px side padding), `xs` unchanged. Variants: `default` = Primary (filled
 * --accent-primary, hover --color-interactive-hover); `outline` and `secondary` = Secondary, neutral
 * (1px --button-secondary-line hairline, no fill, --ink-body text; hover grey-100 with a grey-400 edge, press grey-200; only Primary is blue); `ghost` = Ghost (--ink-secondary,
 * hover --surface-panel); `destructive` = Danger (--status-bad, hover red-600); `link` unchanged.
 * Disabled is 50% opacity (the DS grey disabled colours are replaced by the enabled colours), and
 * focus is the 2px / 2px-offset accent outline instead of the DS ring.
 *
 * What stays local (no DS equivalent): the icon-only square sizes, the inline-svg sizing rules,
 * and `disabled:pointer-events-none` (kept for behaviour: tooltips on a wrapper of a disabled
 * button depend on pointer events passing through).
 */
import { Button as ButtonPrimitive } from "@base-ui/react/button"
import type { VariantProps } from "class-variance-authority"
import { buttonClasses, type ButtonSize, type ButtonVariant } from "@scripsteam/scrips-signal-ds"

import { cn } from "@/lib/utils"

type LocalVariant = "default" | "outline" | "secondary" | "ghost" | "destructive" | "link"
type LocalSize = "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg"

/** shadcn variant name -> DS variant. */
const VARIANT_TO_DS: Readonly<Record<LocalVariant, ButtonVariant>> = Object.freeze({
  default: "primary",
  outline: "secondary", // DS secondary IS the outlined button
  secondary: "secondary", // designer: Secondary = neutral outline (was filled grey = DS neutral)
  ghost: "tertiary", // transparent, no border
  destructive: "destructive",
  link: "link",
})

/** shadcn size name -> DS size, or null where the DS has no step (icon-only squares). */
const SIZE_TO_DS: Readonly<Record<LocalSize, ButtonSize | null>> = Object.freeze({
  default: "sm", // Small: 32px
  xs: "row", // 24px -> 20px (closest DS step)
  sm: "sm", // Small: 32px
  lg: "md", // designer Medium: 40px (DS md is 44px on mobile, 40px from md up; h-10 forces 40)
  icon: null,
  "icon-xs": null,
  "icon-sm": null,
  "icon-lg": null,
})

/** The app-wide button type, merged after the DS recipe so it wins. */
const TYPE = "text-sm font-medium tracking-normal"
/**
 * The app-wide corner. Not applied to `link`: the DS link has no box and sets `rounded-none`
 * deliberately, so the recipe's own value stands there.
 */
const CORNER = "rounded-[var(--radius-md)]"

/** Designer geometry, merged after the DS size step: Small 32 / px-3, Medium 40 / px-4. */
const SIZE_GEOMETRY: Readonly<Record<LocalSize, string>> = Object.freeze({
  default: "h-8 px-3",
  xs: "",
  sm: "h-8 px-3",
  lg: "h-10 px-4",
  icon: "",
  "icon-xs": "",
  "icon-sm": "",
  "icon-lg": "",
})

/** Focus: the app-wide 2px accent outline at 2px offset replaces the DS 2px ring. */
const FOCUS =
  "focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-solid focus-visible:outline-2 " +
  "focus-visible:outline-offset-2 focus-visible:outline-brand-primary"

/**
 * Designer colours per local variant, merged after the recipe. Each also restores the ENABLED
 * colours on `:disabled` / `[aria-disabled]` (the DS swaps them for a grey fill); the dimming is
 * `disabled:opacity-50` in STRUCTURE instead.
 */
const VARIANT_LOOK: Readonly<Record<LocalVariant, string>> = Object.freeze({
  default:
    "disabled:bg-[var(--color-brand-primary)] disabled:text-[var(--color-text-inverse)] " +
    "aria-disabled:bg-[var(--color-brand-primary)] aria-disabled:text-[var(--color-text-inverse)]",
  outline:
    "border-[var(--button-secondary-line)] bg-transparent text-[var(--ink-body)] " +
    "hover:border-[var(--button-secondary-hover-line)] hover:bg-[var(--button-secondary-hover-bg)] active:bg-[var(--button-secondary-press-bg)] " +
    "disabled:border-[var(--button-secondary-line)] disabled:bg-transparent disabled:text-[var(--ink-body)] " +
    "aria-disabled:border-[var(--button-secondary-line)] aria-disabled:bg-transparent aria-disabled:text-[var(--ink-body)]",
  secondary:
    "border-[var(--button-secondary-line)] bg-transparent text-[var(--ink-body)] " +
    "hover:border-[var(--button-secondary-hover-line)] hover:bg-[var(--button-secondary-hover-bg)] active:bg-[var(--button-secondary-press-bg)] " +
    "disabled:border-[var(--button-secondary-line)] disabled:bg-transparent disabled:text-[var(--ink-body)] " +
    "aria-disabled:border-[var(--button-secondary-line)] aria-disabled:bg-transparent aria-disabled:text-[var(--ink-body)]",
  ghost:
    "text-[var(--ink-secondary)] hover:bg-[var(--surface-panel)] " +
    "disabled:text-[var(--ink-secondary)] aria-disabled:text-[var(--ink-secondary)]",
  destructive:
    "bg-[var(--status-bad)] text-[var(--color-text-inverse)] hover:bg-[var(--color-red-600)] " +
    "disabled:bg-[var(--status-bad)] disabled:border-transparent disabled:text-[var(--color-text-inverse)] " +
    "aria-disabled:bg-[var(--status-bad)] aria-disabled:border-transparent aria-disabled:text-[var(--color-text-inverse)]",
  link: "",
})

const STRUCTURE =
  "group/button shrink-0 whitespace-nowrap select-none bg-clip-padding disabled:pointer-events-none " +
  "disabled:opacity-50 aria-disabled:opacity-50 " +
  "aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 " +
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"

/** Geometry the DS recipe does not provide. */
const SIZE_LOCAL: Readonly<Record<LocalSize, string>> = Object.freeze({
  default: "",
  xs: "[&_svg:not([class*='size-'])]:size-3",
  sm: "[&_svg:not([class*='size-'])]:size-4",
  lg: "",
  icon: "size-8 p-0",
  "icon-xs": "size-6 p-0 [&_svg:not([class*='size-'])]:size-3",
  "icon-sm": "size-8 p-0",
  "icon-lg": "size-10 p-0",
})

type ButtonVariantsProps = {
  variant?: LocalVariant | null
  size?: LocalSize | null
  className?: string
  class?: string
}

function buttonVariants({ variant, size, className, class: klass }: ButtonVariantsProps = {}): string {
  // cva semantics, preserved from the old recipe: `undefined` means "use the default",
  // an explicit `null` means "emit no classes for this axis".
  const v = variant === undefined ? "default" : variant
  const s = size === undefined ? "default" : size
  return cn(
    buttonClasses({ variant: v === null ? null : VARIANT_TO_DS[v], size: s === null ? null : SIZE_TO_DS[s] }),
    TYPE,
    v === "link" ? undefined : CORNER,
    v === null ? undefined : VARIANT_LOOK[v],
    s === null || v === "link" ? undefined : SIZE_GEOMETRY[s],
    FOCUS,
    STRUCTURE,
    s === null ? undefined : SIZE_LOCAL[s],
    klass,
    className,
  )
}

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      // base-ui also accepts a state function for className; the previous cva path dropped it
      // (clsx ignores functions), so this keeps that behaviour exactly.
      className={buttonVariants({ variant, size, className: typeof className === "string" ? className : undefined })}
      {...props}
    />
  )
}

export { Button, buttonVariants }
